/* global define */
/**
 * The model for the LSCloud /Account/SalePayment/:id REST endpoint
 *
 * Copyright 2014 LightSpeed Retail. All rights reserved.
 * Created by kurtbergeron on 2014-09-19.
 */
import RetailModel from 'app/src/data/models/RetailModel';
import PaymentType from 'app/src/data/models/PaymentType';
import URLHelper from 'app/src/data/models/utils/URLHelper';
import Format from 'app/src/data/Format';

export default class SalePayment extends RetailModel {
  // Server uses the saleID field as the model's unique identifier. This
  // informs the Backbone.Model of this fact.
  get idAttribute() {
    return 'salePaymentID';
  }

  get defaults() {
    return {
      amount: Format.parseDecimal(0),
      createTime: '',
      archived: false,
      saleID: 0,
      paymentTypeID: 0,
      ccChargeID: 0,
      registerID: 0,
      employeeID: 0,
      creditAccountID: 0,
    };
  }

  /**
   * Return the url for the Sale endpoint
   */
  url() {
    let URL = URLHelper.getAccountURL('/SalePayment');
    if (!this.isNew()) {
      URL += `/${this.id}`;
    }
    return `${URL}.json`;
  }

  get retailResponseName() {
    return 'SalePayment';
  }

  /**
   * Overridden to parse out the superfluous json that the LSCloud returns us
   * @param response - the json object returned from the fetch call before this object has initialized from it
   * @returns {*} the json object that this object will initialize itself from
   */
  parse(response) {
    let parsedResponse = super.parse(response);

    // the Cloud API has a tendency to send all values as strings so let's make sure they're of the proper type
    parsedResponse.salePaymentID = Format.parseInt(parsedResponse.salePaymentID);
    parsedResponse.amount = Format.parseDecimal(parsedResponse.amount);
    parsedResponse.tipAmount = Format.parseDecimal(parsedResponse.tipAmount);
    parsedResponse.archived = Format.parseBoolean(parsedResponse.archived);
    parsedResponse.saleID = Format.parseInt(parsedResponse.saleID);
    parsedResponse.paymentTypeID = Format.parseInt(parsedResponse.paymentTypeID);
    parsedResponse.refPaymentID = Format.parseInt(parsedResponse.refPaymentID);
    parsedResponse.ccChargeID = Format.parseInt(parsedResponse.ccChargeID);
    parsedResponse.registerID = Format.parseInt(parsedResponse.registerID);
    parsedResponse.employeeID = Format.parseInt(parsedResponse.employeeID);
    parsedResponse.creditAccountID = Format.parseInt(parsedResponse.creditAccountID);

    return parsedResponse;
  }

  /**
   * Overridden to remove the archived property when it's false since the API seems to have a bug
   * where it thinks you're trying to un-archive a payment in that case.
   */
  toJSON(options) {
    let attributes = super.toJSON(options);

    if (attributes.archived === false) {
      delete attributes.archived;
    }

    return attributes;
  }

  /**
   * Retrieve the PaymentType model form the SalePayment.
   *
   * @returns {PaymentType}   The SalePayment's PaymentType or undefined if not present.
   */
  getPaymentType() {
    return this.getRelation('PaymentType', PaymentType);
  }

  /**
   * Retrieve the saleLineID of the SalePayment.
   *
   * @returns {number}    The saleLineID of the payment if it has a relation to a sale line or 0 otherwise.
   */
  getSaleLineID() {
    let saleLineID = 0;
    if (this.has('SaleAccounts') && this.get('SaleAccounts').SaleAccount) {
      saleLineID = Format.parseInt(this.get('SaleAccounts').SaleAccount.saleLineID || 0);
    }

    return saleLineID;
  }

  /**
   * Determine if the SalePayment is a cash payment.
   *
   * @returns {*|boolean}     True if the payment is cash payment
   */
  isCash() {
    return this.getPaymentType().isCash();
  }

  /**
   * Determine if the SalePayment is a gift card payment.
   *
   * @returns {*|boolean}     True if the payment is a gift card payment
   */
  isGiftCard() {
    return this.getPaymentType().isGiftCard();
  }
}
